import { Fragment, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { InfoTooltip } from '@/components/InfoTooltip';
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from '@/components/ui/breadcrumb';
import { cn } from '@/lib/utils';

export interface BreadcrumbSegment {
  label: React.ReactNode;
  href?: string;
}

export function PageBreadcrumbs({
  items,
  info,
  infoLabel,
}: {
  items: BreadcrumbSegment[];
  /**
   * What the page is for, in a tooltip on an info icon after the last crumb (the page's title).
   * PageHeader's `info` passes it here.
   */
  info?: ReactNode;
  /** Names what `info` explains, for screen readers. Defaults to the last crumb when it is text. */
  infoLabel?: string;
}) {
  if (items.length === 0) return null;

  return (
    <Breadcrumb className="min-w-0">
      <BreadcrumbList>
        {items.map((item, index) => {
          const isLast = index === items.length - 1;
          const title = typeof item.label === 'string' ? item.label : undefined;
          return (
            <Fragment key={index}>
              {index > 0 ? <BreadcrumbSeparator className="shrink-0" /> : null}
              <BreadcrumbItem
                // With an info icon the crumb itself truncates, so the icon's focus ring isn't clipped.
                className={isLast ? cn('min-w-0', !info && 'overflow-hidden') : 'shrink-0'}
              >
                {isLast || !item.href ? (
                  <BreadcrumbPage className="truncate" title={title}>
                    {item.label}
                  </BreadcrumbPage>
                ) : (
                  <BreadcrumbLink asChild>
                    <Link to={item.href} data-hint={title}>
                      {item.label}
                    </Link>
                  </BreadcrumbLink>
                )}
                {isLast && info ? (
                  <InfoTooltip label={infoLabel ?? title}>{info}</InfoTooltip>
                ) : null}
              </BreadcrumbItem>
            </Fragment>
          );
        })}
      </BreadcrumbList>
    </Breadcrumb>
  );
}
